import { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

interface SingleImageUploadProps {
  /** Current image URL (full URL or static path). Empty/undefined shows the placeholder. */
  currentImage?: string;
  /** Endpoint that accepts a `file` FormData field and persists the image; returns { data: { url } }. */
  uploadUrl: string;
  /** Endpoint that clears/removes the image. */
  deleteUrl: string;
  label?: string;
  /** Called with the new URL after upload, or '' after delete. */
  onChange?: (url: string) => void;
}

/**
 * Reusable single-image control for column-backed images (e.g. a section background).
 * Shows the current image with a remove button, or an upload placeholder when empty.
 * Upload and delete are immediate (persisted by the given endpoints), not tied to a form Save.
 */
export function SingleImageUpload({
  currentImage,
  uploadUrl,
  deleteUrl,
  label = 'Image',
  onChange,
}: SingleImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid format. Use JPEG, PNG, WebP, or GIF.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File too large (max 5MB).');
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(uploadUrl, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || 'Upload failed');
      }
      const url = data?.data?.url as string;
      setPreview(url);
      onChange?.(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [uploadUrl, onChange]);

  const handleRemove = useCallback(async () => {
    if (!confirm('Remove this image?')) return;
    setError(null);
    setBusy(true);
    try {
      const response = await fetch(deleteUrl, { method: 'DELETE', credentials: 'include' });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error?.message || 'Delete failed');
      }
      setPreview(null);
      onChange?.('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }, [deleteUrl, onChange]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      {preview ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt={label}
            className="w-full max-w-md h-44 object-cover rounded-lg border border-gray-200"
            onError={() => setPreview(null)}
          />
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            title="Remove image"
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 text-xs bg-white/90 text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" /> Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="w-full max-w-md flex flex-col items-center justify-center gap-2 h-44 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gold-400 hover:text-gold-600 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImageIcon className="w-6 h-6" />}
          <span className="text-sm font-medium">Click to upload</span>
          <span className="text-xs text-gray-400">JPEG, PNG, WebP or GIF (max 5MB)</span>
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
